import { RiFireFill, RiPrinterFill } from '@remixicon/react'
import { format } from 'date-fns'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import QRCode from 'react-qr-code'
import { useReactToPrint } from 'react-to-print'

import { ImageWithFallback } from '../common/image-with-fallback'
import { Button } from '../ui/button'
import { NumberInput } from '../ui/number-input'

import type { Order } from '@/api/orders/orders-types'
import { Card } from '@/components/ui/card'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/components/ui/carousel'
import { Separator } from '@/components/ui/separator'
import { DATE_FORMATS } from '@/constants/app'
import { cn } from '@/lib/utils'
import { usePrintStickers } from '@/store/print-stickers'
import { formatSkidSizeWithUnit } from '@/utils/order-skids'
import { formatWeight } from '@/utils/weight'

/** `48 x 120 in · 282.2 lb` — printed under the skid line and shown in the preview. */
function formatSkidSizeLine(size: string | null | undefined, weight: number | null | undefined) {
  return [formatSkidSizeWithUnit(size), weight != null ? `${formatWeight(weight)} lb` : null]
    .filter(Boolean)
    .join(' · ')
}

function packagingStickerUrl(order: Order, skidId?: number) {
  const params = new URLSearchParams()
  params.set('search', order.invoice)
  params.set('ship-date', 'all')
  if (skidId != null) params.set('skid', String(skidId))
  return `${window.location.origin}/packaging?${params.toString()}`
}

interface ShippingStickersProps {
  order: Order
  /** When printing from a skid, QR opens packaging with this skid panel. */
  skidId?: number
  skidLabel?: string
  /** Skid position within this order (1-based). Used for the "Skid X of Y" label. */
  skidIndex?: number
  skidTotal?: number
  /** `48 x 120` in inches, printed under the skid line. */
  skidSize?: string | null
  /** Tally of bundle weights on the skid, in lb. */
  skidWeight?: number | null
  /** Print all shipping units in one go — overrides count + single-skid inputs. */
  skids?: { id: number; label: string; size?: string | null; weight?: number | null }[]
}

const TAG_FONT_MAX = 34
const TAG_FONT_MIN = 24

/** Single line if possible: shrinks font down to TAG_FONT_MIN, then wraps. Never truncates. */
function FitTagLine({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let size = TAG_FONT_MAX
    el.style.whiteSpace = 'nowrap'
    el.style.fontSize = `${size}px`
    while (el.scrollWidth > el.clientWidth && size > TAG_FONT_MIN) {
      size -= 1
      el.style.fontSize = `${size}px`
    }
    if (el.scrollWidth > el.clientWidth) el.style.whiteSpace = 'normal'
  }, [text])
  return (
    <div ref={ref} className='leading-none' style={{ fontSize: TAG_FONT_MAX }}>
      {text}
    </div>
  )
}

const PrintableStickerContent = React.forwardRef<
  HTMLDivElement,
  {
    order: Order
    totalStickers: number
    skidId?: number
    skidLabel?: string
    skidIndex?: number
    skidTotal?: number
    skidSize?: string | null
    skidWeight?: number | null
    skids?: { id: number; label: string; size?: string | null; weight?: number | null }[]
  }
>(
  (
    { order, totalStickers, skidId, skidLabel, skidIndex, skidTotal, skidSize, skidWeight, skids },
    ref
  ) => {
  const hasList = skids && skids.length > 0
  const stickerArray = hasList ? skids! : Array.from({ length: totalStickers })
  const listTotal = hasList ? skids!.length : totalStickers

  return (
      <div ref={ref}>
        {stickerArray.map((entry, index) => {
          const perSkidId = hasList ? (entry as { id: number }).id : skidId
          const perSkidLabel = hasList ? (entry as { label: string }).label : skidLabel
          const perSkidIndex = hasList ? index + 1 : skidIndex
          const perSkidTotal = hasList ? listTotal : skidTotal
          const perSkidSize = hasList ? (entry as { size?: string | null }).size : skidSize
          const perSkidWeight = hasList
            ? ((entry as { weight?: number | null }).weight ?? null)
            : (skidWeight ?? null)
          const qrValue = packagingStickerUrl(order, perSkidId)
          return (
          <React.Fragment key={hasList ? (entry as { id: number }).id : index}>
            <Card
              className='mx-auto gap-2.5 rounded-md border p-5 font-medium text-[#000]'
              style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
            >
              <div className='flex size-full items-start gap-3'>
                <div className='flex size-full min-w-0 flex-col space-y-0'>
                  <img
                    className='-mt-2 -ml-3.5 w-52 grayscale'
                    src='/maco-50-logo.svg'
                    alt='Maco enterprises inc'
                  />
                  <div className='space-y-2'>
                    <div className='text-base leading-tight'>www.macoenterprises.ca</div>
                    <div className='text-base leading-none'>519-638-2746</div>
                  </div>

                  <div className='mt-auto flex flex-col gap-3.5'>
                    <div className='space-y-5'>
                      <div className='text-4xl leading-none'>{order?.customer}</div>
                      <FitTagLine text={`Tag: ${order?.ship_tag ?? '-'}`} />
                      <div className='space-y-1.5'>
                        {perSkidLabel ? (
                          <div className='text-4xl leading-none'>
                            {perSkidLabel}
                            {perSkidIndex != null && perSkidTotal != null
                              ? ` (${perSkidIndex} of ${perSkidTotal})`
                              : null}
                          </div>
                        ) : null}
                        {perSkidSize || perSkidWeight != null ? (
                          <div className='text-2xl leading-none'>
                            {formatSkidSizeLine(perSkidSize, perSkidWeight)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className='text-sm leading-none text-[#000]'>Shipping</div>
                    <div className='flex items-center gap-2 leading-none'>
                      <span className='text-base'>
                        {order.ship_date ? format(order.ship_date, DATE_FORMATS.dots) : '-'} {order?.ship_time}
                      </span>
                      <Separator
                        className='h-5'
                        orientation='vertical'
                      />
                      <span
                        className={cn(
                          'flex items-center gap-0.5 rounded-sm',
                          order?.ship_type === 'SET OUTSIDE'
                            ? 'bg-[#000] py-0.5 pr-1 pl-0.5 text-xs leading-none text-[#fff]'
                            : 'text-base'
                        )}
                      >
                        {order?.ship_type === 'SET OUTSIDE' ? (
                          <RiFireFill className='-mt-px size-2.5' />
                        ) : null}

                        <span className='mt-px'>{order?.ship_type ?? '-'}</span>
                      </span>
                    </div>
                    {/* </div> */}
                  </div>
                </div>

                <div className='flex h-full flex-col items-end justify-between'>
                  <div className='text-4xl'>#{order.invoice}</div>
                  <div className='text-4xl'>
                    {index + 1} of {listTotal}
                  </div>
                  <div className='size-25'>
                    <QRCode
                      onClick={() => window.open(qrValue)}
                      className='size-full rounded-sm'
                      value={qrValue}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Add a page break after each sticker, but not after the last one */}
            {index < listTotal - 1 && <div className='page-break-after'></div>}
          </React.Fragment>
        )})}
      </div>
    )
  }
)

PrintableStickerContent.displayName = 'PrintableStickerContent'

export const ShippingStickers = ({
  order,
  skidId,
  skidLabel,
  skidIndex,
  skidTotal,
  skidSize,
  skidWeight,
  skids
}: ShippingStickersProps) => {
  const printAllMode = skids != null && skids.length > 0
  const qrPreview = packagingStickerUrl(order, skidId)

  const [stickersToPrint, setStickersToPrint] = useState(1)

  const [_, setIsPrinting] = useState(false)

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(1)
  const carouselCount = printAllMode ? skids!.length : 0

  useEffect(() => {
    if (!api) return
    setCurrent(api.selectedScrollSnap() + 1)
    api.on('select', () => setCurrent(api.selectedScrollSnap() + 1))
  }, [api])

  // Subset of skids passed to the printable node — null = legacy single-skid mode.
  const [skidsForPrinting, setSkidsForPrinting] = useState<
    { id: number; label: string }[] | null
  >(null)

  const { setPrintDetail, setPrintOrder } = usePrintStickers()

  const contentRef = useRef<HTMLDivElement>(null)

  const reactToPrintFn = useReactToPrint({
    contentRef,
    onBeforePrint: async () => {
      setIsPrinting(true)
      setTimeout(() => {
        setPrintDetail(null)
        setPrintOrder(null)
      }, 200)
    },
    onAfterPrint: async () => {
      setIsPrinting(false)
      setSkidsForPrinting(null)
    },
    pageStyle: `
  @page {
    size: 6in 4in; /* Sticker size */
    margin: 0;
  }

  @media print {
    html, body {
      margin: 0;
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Printable card exact size */
    .printable-card {
      width: 6in !important;
      height: 4in !important;
      box-sizing: border-box !important;
      padding: 0.625rem !important; /* match p-2.5 */
      margin: 0 auto !important;
      border: none !important;
      border-radius: 0 !important;
      background: #fff !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
    }

    /* Page break after each sticker */
    .page-break-after {
      page-break-after: always;
      break-after: page;
      height: 0 !important;
      overflow: hidden !important;
    }

    /* Separator (vertical line) */
    .h-5[orientation="vertical"] {
      background-color: #000 !important;
      width: 1px !important;
      height: 1.25rem !important; /* 20px */
      display: inline-block !important;
    }

    /* Ensure consistent line-height */
    .leading-none { line-height: 1 !important; }

    /* Adjust gaps/margins for print */
    .gap-0\\.5 { gap: 0.05cm !important; }
    .gap-1 { gap: 0.1cm !important; }
    .gap-2 { gap: 0.2cm !important; }
    .gap-2\\.5 { gap: 0.25cm !important; }
    .gap-3\\.5 { gap: 0.35cm !important; }
    .space-y-2 > *:not(:last-child) { margin-bottom: 0.2cm !important; }
    .space-y-4 > *:not(:last-child) { margin-bottom: 0.4cm !important; }

    /* Explicit sizes */
    .w-48 { width: 192px !important; }
    /* Match the bundle sticker QR footprint — handheld scanners fit this in frame. */
    .size-25 { width: 1.05in !important; height: 1.05in !important; }
    .size-full { width: 100% !important; height: 100% !important; }

    /* QR code */
    .rounded-sm { border-radius: 2px !important; }

    /* Hide interactive UI */
    .no-print { display: none !important; }
  }
`,
    suppressErrors: process.env.NODE_ENV !== 'production'
  })

  const printCurrentSkid = () => {
    if (!printAllMode) return
    const target = skids![current - 1]
    if (!target) return
    setSkidsForPrinting([target])
    setTimeout(() => reactToPrintFn(), 0)
  }

  const printAllSkids = () => {
    if (!printAllMode) return
    setSkidsForPrinting(skids!)
    setTimeout(() => reactToPrintFn(), 0)
  }

  const renderPreviewCard = (
    label: string | undefined,
    indexLabel: { current: number; total: number } | null,
    qrValue: string,
    sizeLine?: string
  ) => (
    <Card
      className='no-print mx-auto gap-2.5 rounded-md border p-5 font-medium text-[#000]'
      style={{ width: '6in', height: '4in', boxSizing: 'border-box' }}
    >
      <div className='flex size-full items-start'>
        <div className='flex size-full min-w-0 flex-col space-y-0'>
          <ImageWithFallback
            className='-mt-2 -ml-3.5 w-52 grayscale'
            src='/maco-50-logo.svg'
            alt='Maco enterprises inc'
          />

          <div className='space-y-2'>
            <div className='text-base leading-tight'>www.macoenterprises.ca</div>
            <div className='text-base leading-none'>519-638-2746</div>
          </div>

          <div className='mt-auto flex w-full flex-col justify-between gap-3.5'>
            <div className='space-y-5'>
              <div className='text-4xl leading-none'>{order?.customer}</div>
              <FitTagLine text={`Tag: ${order?.ship_tag ?? '-'}`} />
              <div className='space-y-1.5'>
                {label ? (
                  <div className='text-4xl leading-none'>
                    {label}
                    {indexLabel ? ` (${indexLabel.current} of ${indexLabel.total})` : null}
                  </div>
                ) : null}
                {sizeLine ? <div className='text-2xl leading-none'>{sizeLine}</div> : null}
              </div>
            </div>
            <div className='space-y-4'>
              <div className='flex flex-col gap-0.5'>
                <div className='text-sm leading-none text-[#000]'>Shipping</div>
                <div className='flex items-center gap-2 leading-none'>
                  <span className='text-base'>
                    {order.ship_date ? format(order.ship_date, DATE_FORMATS.dots) : '-'} {order?.ship_time}
                  </span>
                  <Separator
                    className='h-5'
                    orientation='vertical'
                  />
                  <span
                    className={cn(
                      'flex items-center gap-0.5 rounded-sm',
                      order?.ship_type === 'SET OUTSIDE'
                        ? 'bg-[#000] py-0.5 pr-1 pl-0.5 text-xs leading-none text-[#fff]'
                        : 'text-base'
                    )}
                  >
                    {order?.ship_type === 'SET OUTSIDE' ? (
                      <RiFireFill className='-mt-px size-2.5' />
                    ) : null}

                    <span className='mt-px'>{order?.ship_type ?? '-'}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='flex h-full flex-col items-end justify-between'>
          <div className='text-4xl'>#{order.invoice}</div>
          <div className='text-4xl'>
            {indexLabel ? `${indexLabel.current} of ${indexLabel.total}` : `1 of ${stickersToPrint}`}
          </div>

          <div className='size-25'>
            <QRCode
              onClick={() => window.open(qrValue)}
              className='size-full rounded-sm'
              value={qrValue}
            />
          </div>
        </div>
      </div>
    </Card>
  )

  return (
    <div className='w-full'>
      {printAllMode ? (
        <Carousel
          className='no-print mx-auto w-150'
          setApi={setApi}
        >
          <CarouselContent>
            {skids!.map((entry, index) => (
              <CarouselItem key={entry.id}>
                {renderPreviewCard(
                  entry.label,
                  { current: index + 1, total: carouselCount },
                  packagingStickerUrl(order, entry.id),
                  formatSkidSizeLine(entry.size, entry.weight)
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      ) : (
        <div className='no-print mx-auto w-2/3'>
          {renderPreviewCard(
            skidLabel,
            skidIndex != null && skidTotal != null
              ? { current: skidIndex, total: skidTotal }
              : null,
            qrPreview,
            formatSkidSizeLine(skidSize, skidWeight)
          )}
        </div>
      )}

      {/* This is the content that will be passed to react-to-print.
          Kept off-screen (not display:none) so FitTagLine can measure text width. */}
      <div style={{ position: 'absolute', left: -10000, top: 0, visibility: 'hidden' }}>
        <PrintableStickerContent
          ref={contentRef}
          order={order}
          totalStickers={stickersToPrint}
          skidId={skidId}
          skidLabel={skidLabel}
          skidIndex={skidIndex}
          skidTotal={skidTotal}
          skidSize={skidSize}
          skidWeight={skidWeight}
          skids={skidsForPrinting ?? skids}
        />
      </div>

      <div className='mt-4 flex w-full items-end gap-3.5'>
        {printAllMode ? (
          <>
            <Button
              className='flex-1'
              variant='outline'
              onClick={printCurrentSkid}
            >
              <RiPrinterFill />
              <span>
                Print Current sticker — {current} of {carouselCount}
              </span>
            </Button>
            <Button
              className='flex-1'
              onClick={printAllSkids}
            >
              <RiPrinterFill />
              <span>Print All {carouselCount} stickers</span>
            </Button>
          </>
        ) : (
          <>
            <div className='flex flex-1 flex-col gap-0.5'>
              <div className='text-base text-[#000]'>Stickers to print</div>
              <NumberInput
                className='[&_input]:bg-white-100 [&_button]:bg-white-100 [&_button]:hover:!bg-white-100'
                onChange={setStickersToPrint}
                value={stickersToPrint}
                minValue={1}
              />
            </div>
            <Button
              className='flex-1'
              onClick={reactToPrintFn}
            >
              <RiPrinterFill />
              <span>Print {stickersToPrint} sticker(s)</span>
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
